/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');

      // AI Commerce Search (step-06): AI-ranked products + autocomplete in the dropdown. When the
      // endpoints are absent — or AI errors / returns nothing — we fall back to native predictive.
      this.aiEndpoint = this.dataset.aiEndpoint || '';
      this.aiNgrokSkip = this.aiEndpoint.indexOf('ngrok') !== -1;
      this.aiRegion = this.resolveAIRegion();
      this.aiLocale = this.resolveAILocale();
      this.aiTimeout = parseInt(this.dataset.aiTimeout, 10) || 6000; // abort a slow AI fetch → keep native
      this.aiCache = {};            // AI product rows (HTML) keyed by region+locale+query
      this.cardCache = {};          // per-product Section-rendered <li> keyed by region+url
      this.aiController = null;     // in-flight AbortController (cancelled per keystroke / timeout)

      // Recent searches config
      this.recentSearchesKey = 'recent_searches';
      this.maxRecentSearches = 7;
      this.recentSearchesVisible = false;

      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
      this.results.addEventListener('click', this.handleSuggestionClick.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      } else {
        // Save search term to recent searches on valid submit
        this.saveRecentSearch(this.getQuery());
      }
    }

    /**
     * Handles clicks on predictive suggestion links, saving the term to recent searches.
     * @param {object} evt - Event object.
     */
    handleSuggestionClick(evt) {
      // Query suggestion clicked — save its text
      const suggestionLink = evt.target.closest('.predictive-result--suggestion');
      if (suggestionLink) {
        const term = suggestionLink.textContent.trim();
        if (term) this.saveRecentSearch(term);
        return;
      }

      // Product / collection / article / page result clicked (not the "view all" button)
      const resultLink = evt.target.closest('.js-search-link');
      if (resultLink && !resultLink.closest('.js-submit')) {
        // Prefer the product title; fall back to the typed query
        const titleEl = resultLink.querySelector('.predictive-result__title');
        const term = titleEl ? titleEl.textContent.trim() : this.getQuery();
        if (term) this.saveRecentSearch(term);
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        // Show recent searches when user clears input
        this.showRecentSearches();
        return;
      }

      // User is typing — switch from recent searches to predictive results
      this.hideRecentSearches();
      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();

      // Always try to show recent searches first on focus
      const recentSearches = this.getRecentSearches();
      if (recentSearches.length) {
        this.showRecentSearches();
        return;
      }

      // No recent searches — fall back to existing behaviour
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term. AI-FIRST: tries AI predictive (when enabled and no
     * product-type filter is active — that defers to native, like the results page does for facets);
     * on AI error / timeout / zero / abort, falls back to native predictive search.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      const productTypeActive = this.productTypeInput && this.productTypeInput.value !== '';
      if (this.aiEndpoint && !productTypeActive) {
        try {
          await this.getAIResults(searchTerm);
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') return;   // newer keystroke superseded this one
          // any other AI error → fall through to native predictive
        }
      }
      return this.getNativeResults(searchTerm);
    }

    /* ------------------------------------------------------------------ AI predictive (step-06) */
    /* AI-FIRST + instant skeleton (the step-05b strategy applied to the dropdown): on a keystroke the
       dropdown shows a shimmer skeleton IMMEDIATELY, then AI-ranked REAL native cards (Section
       Rendering) replace it. Two-stage: the clean cover shows at once; the shimmer only fades in if AI
       takes >~200 ms (warm/cached → straight to cards, no flash). On any AI error / timeout / zero,
       getResults() falls back to native predictive — never a broken or empty dropdown. */

    // Region: explicit ?region= → data-region (storefront locale) → host (.ca/.com). Mirrors Phase 1.
    resolveAIRegion() {
      const explicit = new URLSearchParams(location.search).get('region');
      if (explicit) return explicit.toUpperCase();
      if (this.dataset.region) return this.dataset.region.toUpperCase();
      const h = location.hostname;
      if (/\.ca$/i.test(h)) return 'CA';
      if (/\.com$/i.test(h)) return 'US';
      return '';
    }

    /** Keep in sync with shopify/lib/ai-locale-utils.mjs / ai-search.js */
    resolveAILocale() {
      const explicit = new URLSearchParams(location.search).get('locale');
      if (explicit) return this.normalizeAILocale(explicit);
      if (this.dataset.locale) return this.normalizeAILocale(this.dataset.locale);
      if (window.AICommerceSearch && typeof window.AICommerceSearch.getLocale === 'function') {
        return window.AICommerceSearch.getLocale();
      }
      if (window.Shopify && Shopify.locale) return this.normalizeAILocale(Shopify.locale);
      return this.normalizeAILocale(document.documentElement && document.documentElement.lang);
    }

    normalizeAILocale(raw) {
      if (window.AICommerceSearch && typeof window.AICommerceSearch.normalizeShopLocale === 'function') {
        return window.AICommerceSearch.normalizeShopLocale(raw);
      }
      const s = String(raw == null ? '' : raw).trim().toLowerCase();
      if (!s) return 'en';
      const primary = s.split(/[-_]/)[0];
      if (primary === 'en' || primary === 'fr' || primary === 'es') return primary;
      return 'en';
    }

    /**
     * Render the dropdown from AI results. Throws on AI error / zero products / abort → caller falls
     * back to native. Single AI /search request per keystroke (no wasted native fetch on success).
     * @param {string} term
     */
    async getAIResults(term) {
      this.setLiveRegionLoadingState();
      const region = this.aiRegion;
      const locale = this.aiLocale || this.resolveAILocale();
      this.aiLocale = locale;
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const cacheKey = `${root}:${region}:${locale}:${term.toLowerCase()}`;

      // Cached → straight to AI cards, no skeleton, no flash.
      if (this.aiCache[cacheKey]) {
        this.renderResults(this.buildAIDropdown(this.aiCache[cacheKey], term));
        return;
      }

      // One AbortController per keystroke + a hard timeout, so a slow/hung backend can't block the UI.
      if (this.aiController) this.aiController.abort();
      const controller = new AbortController();
      this.aiController = controller;
      const timer = setTimeout(() => controller.abort(), this.aiTimeout);

      // Show the skeleton IMMEDIATELY; reveal the shimmer only after ~200 ms (two-stage, no flash fast).
      this.renderResults(this.buildSkeleton());
      const skel = this.results.querySelector('.ai-pskel');
      this.skelTimer = setTimeout(() => { if (skel && skel.isConnected) skel.classList.add('is-revealed'); }, 200);

      try {
        let qs = `q=${encodeURIComponent(term)}&pageSize=6`;
        if (region) qs += `&region=${encodeURIComponent(region)}`;
        if (locale) qs += `&locale=${encodeURIComponent(locale)}`;
        var aiHeaders = { Accept: 'application/json' };
        if (this.aiNgrokSkip) aiHeaders['ngrok-skip-browser-warning'] = 'true';
        const r = await fetch(`${this.aiEndpoint}?${qs}`, { headers: aiHeaders, signal: controller.signal });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();

        if (data.attributionToken && data.products && data.products.length) {
          if (window.CloudRetailAttribution) {
            window.CloudRetailAttribution.mergeTokens(data.products, data.attributionToken);
          } else if (window.AICommerceSearch && window.AICommerceSearch.mergeSearchTokens) {
            window.AICommerceSearch.mergeSearchTokens(data.products, data.attributionToken);
          }
          if (data.region && window.CloudRetailAttribution) {
            window.CloudRetailAttribution.setRegion(data.region);
          }

          // Same search event as AI results page — only when attribution token is present.
          const region = data.region || this.aiRegion || '';
          const entity = region && region.toLowerCase();
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ cloud_retail: null });
          window.dataLayer.push({
            event: 'cloud_retail_search',
            cloud_retail: {
              eventType: 'search',
              eventTime: new Date().toISOString(),
              visitorId: window.CloudRetailAttribution && window.CloudRetailAttribution.getVisitorId
                ? window.CloudRetailAttribution.getVisitorId()
                : undefined,
              searchQuery: term,
              attributionToken: data.attributionToken,
              productDetails: data.products.map((p) => ({ product: { id: String(p.id) } })),
              ...(region && {
                entity,
                attributes: { market: { text: [region] } },
              }),
              uri: location.href,
            },
          });
        }
        if (data.region) this.aiRegion = data.region;

        if (!data || !Array.isArray(data.products) || data.products.length === 0) throw new Error('no AI products');

        // Render each AI product as a REAL native predictive card (Section Rendering), preserving order.
        const lis = await Promise.all(data.products.map((p) => this.fetchPredictiveCard(p, region, controller.signal)));
        const rows = lis.filter(Boolean).join('');
        if (!rows) throw new Error('no renderable AI cards'); // all skipped/404 → native fallback

        this.aiCache[cacheKey] = rows;
        if (this.getQuery() === term) {
          this.renderResults(this.buildAIDropdown(rows, term)); // ignore if typed on
        }
      } finally {
        clearTimeout(this.skelTimer);
        clearTimeout(timer);
      }
    }

    // Loading state — a few shimmer rows in a native-classed shell. Namespaced `ai-pskel-*` so it can
    // NEVER collide with the search-results-page skeleton (ai-search.css `.ai-skel-*`). Not
    // keyboard-navigable (no role=option / js-search-link) until the real cards land.
    buildSkeleton() {
      let rows = '';
      for (let i = 0; i < 5; i++) {
        rows +=
          '<li class="ai-pskel-row" style="--ai-pskel-i:' + i + '">'
          + '<span class="ai-pskel-media"></span>'
          + '<span class="ai-pskel-text"><span class="ai-pskel-line ai-pskel-line--t"></span><span class="ai-pskel-line ai-pskel-line--p"></span></span>'
          + '</li>';
      }
      return (
        '<div class="predictive-search absolute bg-theme-bg text-theme-text text-start">'
        + '<div class="predictive-search__scroller overflow-auto">'
        + '<div class="predictive-search__results flex flex-col">'
        + '<div class="ai-pskel predictive-search__tabpanel" role="status" aria-busy="true" aria-label="Loading results">'
        + '<ul class="ai-pskel-list" aria-hidden="true">' + rows + '</ul></div>'
        + '</div></div></div>'
      );
    }

    // The real dropdown: AI cards (Section-rendered native rows) + the native "View all" item.
    buildAIDropdown(rowsHtml, term) {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const viewAllHref = root + 'search?q=' + encodeURIComponent(term);
      const viewAllLabel = this.dataset.i18nViewAll || 'View all search results';
      const viewAll =
        '<li class="predictive-search__item js-submit" role="option">'
        + '<a class="predictive-search__view-all link w-full text-start focus-inset has-ltr-icon js-search-link" href="' + viewAllHref + '">'
        + viewAllLabel + '</a></li>';
      return (
        '<div class="predictive-search absolute bg-theme-bg text-theme-text text-start">'
        + '<div class="predictive-search__scroller overflow-auto">'
        + '<div class="predictive-search__results flex flex-col">'
        + '<div class="predictive-search__tabpanel focus-inset" role="tabpanel" tabindex="0">'
        + '<ul class="predictive-search__list" role="list">' + rowsHtml + viewAll + '</ul></div>'
        + '</div></div></div>'
      );
    }

    /**
     * Fetch one product's native predictive card via the Section Rendering API and cache it.
     * Returns the <li> HTML, or null to SKIP (no handle / 404 / unpublished). Mirrors ai-search.js.
     */
    async fetchPredictiveCard(p, region, signal) {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      let handle = null;
      if (p.url && p.url.indexOf('/products/') === 0) {
        handle = p.url.split('/products/')[1].split(/[?#]/)[0];
      } else if (p.handle) {
        handle = String(p.handle).replace(/^\/+/, '');
      }
      if (!handle) return null;
      const u = root + 'products/' + handle;
      const key = `${root}:${region}:${handle}`;
      if (this.cardCache[key]) return this.cardCache[key];
      const sep = u.indexOf('?') > -1 ? '&' : '?';
      try {
        const r = await fetch(`${u}${sep}section_id=ai-predictive-card`, { signal });
        if (r.status === 404) return null;
        if (!r.ok) throw new Error(`card ${r.status}`);
        const li = this.extractLi(await r.text());
        if (!li) return null;
        this.cardCache[key] = li;
        return li;
      } catch (e) {
        if (e && e.name === 'AbortError') throw e;          // let supersede/timeout cancel the batch
        return null;                                        // a single bad card is skipped, not fatal
      }
    }

    // Section Rendering wraps output in <div class="shopify-section …">; return the inner <li>.
    extractLi(html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = (html || '').trim();
      const sec = tmp.querySelector('.shopify-section') || tmp;
      return sec.querySelector('.predictive-search__item') ? sec.innerHTML.trim() : '';
    }

    /**
     * Gets the native predictive results for a search term (Section Rendering API). Used as the
     * fallback when AI is disabled / errors / returns nothing.
     * @param {string} searchTerm - Search query.
     */
    async getNativeResults(searchTerm) {
      this.setLiveRegionLoadingState();

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        if (!resultsEl) throw new Error('predictive section missing in response');
        // GUARD (theme bug): the predictive dropdown must contain ONLY results — never a search
        // form/input. If the response includes one, rendering it injects a nested <predictive-search>
        // that re-initialises and stacks duplicate, cascading search bars (and shows no results).
        // Strip any form / search component before rendering so the dropdown can't recurse.
        resultsEl.querySelectorAll(
          'predictive-search, search-form, form, input[type="search"], .js-search-input, .search__form'
        ).forEach((n) => n.remove());
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        this.close();
        throw error;
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }

    // ==========================================
    // Recent Searches Methods
    // ==========================================

    /**
     * Gets recent searches from localStorage.
     * @returns {Array}
     */
    getRecentSearches() {
      try {
        const searches = localStorage.getItem(this.recentSearchesKey);
        return searches ? JSON.parse(searches) : [];
      } catch (e) {
        return [];
      }
    }

    /**
     * Saves a search term to recent searches.
     * @param {string} term - Search term to save.
     */
    saveRecentSearch(term) {
      if (!term || !term.trim()) return;

      const sanitizedTerm = term.trim();
      let searches = this.getRecentSearches();

      // Remove duplicate if exists (case-insensitive)
      searches = searches.filter(
        (s) => s.toLowerCase() !== sanitizedTerm.toLowerCase()
      );

      // Add to beginning
      searches.unshift(sanitizedTerm);

      // Limit to max entries
      if (searches.length > this.maxRecentSearches) {
        searches = searches.slice(0, this.maxRecentSearches);
      }

      try {
        localStorage.setItem(this.recentSearchesKey, JSON.stringify(searches));
      } catch (e) {
        // localStorage full or unavailable - fail silently
      }
    }

    /**
     * Removes a single recent search by index.
     * @param {number} index - Index of search to remove.
     */
    removeRecentSearch(index) {
      const searches = this.getRecentSearches();
      searches.splice(index, 1);
      try {
        localStorage.setItem(this.recentSearchesKey, JSON.stringify(searches));
      } catch (e) {
        // fail silently
      }
      // Re-render recent searches
      if (searches.length > 0) {
        this.showRecentSearches();
      } else {
        this.hideRecentSearches();
        this.close();
      }
    }

    /**
     * Clears all recent searches.
     */
    clearRecentSearches() {
      try {
        localStorage.removeItem(this.recentSearchesKey);
      } catch (e) {
        // fail silently
      }
      this.hideRecentSearches();
      this.close();
    }

    /**
     * Shows the recent searches dropdown.
     */
    showRecentSearches() {
      const searches = this.getRecentSearches();
      if (!searches.length) return;

      const clockIcon = '<svg class="recent-searches__item-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>';
      const removeIcon = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

      let html = '<div class="recent-searches">';
      html += '<div class="recent-searches__header">';
      html += '<p class="recent-searches__title">Recent Searches</p>';
      html += '<button type="button" class="recent-searches__clear js-clear-recent-searches">Clear All</button>';
      html += '</div>';
      html += '<ul class="recent-searches__list">';

      searches.forEach((term, index) => {
        const escapedTerm = term.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        html += `<li class="recent-searches__item js-recent-search-item" data-term="${escapedTerm}" data-index="${index}">`;
        html += clockIcon;
        html += `<span class="recent-searches__item-text">${escapedTerm}</span>`;
        html += `<button type="button" class="recent-searches__item-remove js-remove-recent-search" data-index="${index}" aria-label="Remove">${removeIcon}</button>`;
        html += '</li>';
      });

      html += '</ul></div>';

      // Wrap in .predictive-search so the theme CSS (predictive-search[open] .predictive-search) makes it visible
      const wrapper = '<div class="predictive-search absolute bg-theme-bg text-theme-text text-start"><div class="predictive-search__scroller overflow-auto">' + html + '</div></div>';

      this.results.innerHTML = wrapper;
      this.recentSearchesVisible = true;
      this.open();

      // Add event listeners for recent search items
      this.results.querySelectorAll('.js-recent-search-item').forEach((item) => {
        item.addEventListener('click', (evt) => {
          // Don't trigger if the remove button was clicked
          if (evt.target.closest('.js-remove-recent-search')) return;
          const term = item.getAttribute('data-term');
          this.input.value = term;
          this.input.setAttribute('value', term);
          this.hideRecentSearches();
          // Trigger search
          this.getResults(term);
          // Also submit the form to navigate to search results
          this.input.closest('form').submit();
        });
      });

      // Add event listeners for remove buttons
      this.results.querySelectorAll('.js-remove-recent-search').forEach((btn) => {
        btn.addEventListener('click', (evt) => {
          evt.stopPropagation();
          const index = parseInt(btn.getAttribute('data-index'), 10);
          this.removeRecentSearch(index);
        });
      });

      // Add event listener for clear all button
      const clearBtn = this.results.querySelector('.js-clear-recent-searches');
      if (clearBtn) {
        clearBtn.addEventListener('click', () => {
          this.clearRecentSearches();
        });
      }
    }

    /**
     * Hides the recent searches dropdown.
     */
    hideRecentSearches() {
      if (this.recentSearchesVisible) {
        this.recentSearchesVisible = false;
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
